import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatErr, fileViewUrl } from "../lib/api";
import { ArrowLeft, Calendar, MapPin, Users, Megaphone, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import AiReportPanel from "../components/AiReportPanel";
import MapEmbed from "../components/MapEmbed";
import { useAuth } from "../context/AuthContext";

export default function AwarenessDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isManager = user?.role === "manager";
  const [a, setA] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/awareness/${id}`)
      .then((r) => setA(r.data))
      .catch((e) => toast.error(formatErr(e)))
      .finally(() => setLoading(false));
  }, [id]);

  const remove = async () => {
    if (!window.confirm(`Delete "${a.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/awareness/${id}`);
      toast.success("Program deleted");
      navigate("/awareness");
    } catch (e) { toast.error(formatErr(e)); }
  };

  if (loading) return <div className="text-brand-muted">Loading…</div>;
  if (!a) return null;

  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-fade-up">
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/awareness"))}
          className="inline-flex items-center gap-2 h-11 px-4 rounded-md border-2 border-brand-primary bg-white text-brand-primary font-bold text-sm shadow-sm hover:bg-brand-primary hover:text-white transition-colors"
          data-testid="back-btn"
          aria-label="Back to previous screen"
        >
          <ArrowLeft className="h-5 w-5" strokeWidth={3} /> Back
        </button>
        {isManager && (
          <div className="flex items-center gap-2">
            <button onClick={() => navigate(`/awareness/${id}/edit`)} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-brand-border bg-white hover:bg-brand-surfaceAlt text-sm font-medium" data-testid="edit-awareness-btn">
              <Pencil className="h-3.5 w-3.5" /> Edit
            </button>
            <button onClick={remove} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-brand-border bg-white hover:bg-brand-surfaceAlt text-sm font-medium text-brand-danger" data-testid="delete-awareness-btn">
              <Trash2 className="h-3.5 w-3.5" /> Delete
            </button>
          </div>
        )}
      </div>

      <div>
        <div className="text-xs uppercase tracking-[0.18em] text-brand-muted font-semibold">{a.topic}</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold mt-1">{a.title}</h1>
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-brand-muted">
          <span className="inline-flex items-center gap-1"><Calendar className="h-4 w-4" /> {a.program_date}</span>
          <span className="inline-flex items-center gap-1"><MapPin className="h-4 w-4" /> {a.village}{a.location_label ? ` · ${a.location_label}` : ""}</span>
          <span className="inline-flex items-center gap-1"><Users className="h-4 w-4" /> {a.attendees} attendees</span>
          {a.latitude && a.longitude && (
            <a target="_blank" rel="noreferrer" href={`https://maps.google.com/?q=${a.latitude},${a.longitude}`} className="inline-flex items-center gap-1 text-brand-primary">
              <MapPin className="h-4 w-4" /> Map
            </a>
          )}
        </div>
      </div>

      {a.photo_paths?.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {a.photo_paths.map((p) => <img key={p} src={fileViewUrl(p)} alt="" className="aspect-square w-full object-cover rounded-md border border-brand-border" />)}
        </div>
      )}

      <Card title="Description">
        <p className="text-sm whitespace-pre-wrap">{a.description || "—"}</p>
      </Card>
      <Card title="Remarks">
        <p className="text-sm whitespace-pre-wrap">{a.remarks || "—"}</p>
      </Card>

      {a.latitude && a.longitude && (
        <Card title="Location">
          <MapEmbed latitude={a.latitude} longitude={a.longitude} label={a.title} />
        </Card>
      )}

      <Card title="AI Field Report">
        <AiReportPanel
          contextType="awareness"
          entityId={a.id}
          photoPaths={a.photo_paths || []}
          initialText={a.ai_report_text || ""}
          initialId={a.ai_report_id || null}
        />
      </Card>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="bg-white border border-brand-border rounded-lg p-5">
      <h2 className="font-heading text-lg font-semibold mb-3 inline-flex items-center gap-2"><Megaphone className="h-4 w-4 text-brand-muted" /> {title}</h2>
      {children}
    </div>
  );
}
